import React, { createContext, useContext, useState, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useClient, PaginatedResponse } from '@/stores/useClient.ts'
import { ProfProject } from '@/models/profProject.ts'
import { ProfTag } from '@/models/profTag.ts'
import { usePortalContext } from '@/pages/portal/Portal.page.tsx'

export interface ProjectsQueryParams {
  page: number
  page_size: number
  search?: string
  o?: string
  tag?: string
}

interface ProjectsContextType {
  projects: ProfProject[]
  totalCount: number
  isLoading: boolean
  error: Error | null
  params: ProjectsQueryParams
  allTags: ProfTag[]
  setPage: (page: number) => void
  setSort: (sort: string) => void
  setSearch: (search: string) => void
  setTag: (tag: string | undefined) => void
  refetch: () => void
}

const ProjectsContext = createContext<ProjectsContextType | null>(null)

export function useProjectsContext() {
  return useContext(ProjectsContext)
}

export function ProjectsProvider({ children }: { children: React.ReactNode }) {
  const { getProjects } = useClient()
  const queryClient = useQueryClient()
  const portalContext = usePortalContext()

  const [params, setParams] = useState<ProjectsQueryParams>({
    page: 1,
    page_size: 20,
  })

  const { data, isLoading, error } = useQuery({
    queryKey: ['projects', params],
    queryFn: async () => {
      const res = await getProjects({
        page: params.page,
        page_size: params.page_size,
        search: params.search,
        o: params.o,
        tag: params.tag,
      })
      return res.data as PaginatedResponse<ProfProject>
    },
  })

  const allTags = portalContext?.account?.tags ?? []

  const setPage = useCallback((page: number) => {
    setParams(prev => ({ ...prev, page }))
  }, [])

  const setSort = useCallback((sort: string) => {
    setParams(prev => ({ ...prev, o: sort || undefined, page: 1 }))
  }, [])

  const setSearch = useCallback((search: string) => {
    setParams(prev => ({ ...prev, search: search || undefined, page: 1 }))
  }, [])

  const setTag = useCallback((tag: string | undefined) => {
    setParams(prev => ({ ...prev, tag, page: 1 }))
  }, [])

  const handleRefetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['projects'] })
  }, [queryClient])

  return (
    <ProjectsContext.Provider value={{
      projects: data?.results ?? [],
      totalCount: data?.count ?? 0,
      isLoading,
      error: error as Error | null,
      params,
      allTags,
      setPage,
      setSort,
      setSearch,
      setTag,
      refetch: handleRefetch,
    }}>
      {children}
    </ProjectsContext.Provider>
  )
}
